// Master Admin → Microsoft Email Settings + Email Logs + Apple Pay
// =====================================================================
// One page that the operator uses to wire SMTP, send a test email,
// audit deliveries, resend failures, and upload the Apple Pay domain
// association file Square requires.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle, CheckCircle2, Loader2, Mail, RefreshCcw, Send,
  ShieldCheck, Upload, XCircle, FileText, Settings as SettingsIcon,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const TABS = [
  { key: "settings", label: "SMTP Settings",  icon: SettingsIcon },
  { key: "logs",     label: "Email Logs",     icon: Mail },
  { key: "applepay", label: "Apple Pay File", icon: ShieldCheck },
];

export default function AdminEmailSettings() {
  const [tab, setTab] = useState("settings");
  return (
    <div className="min-h-screen px-4 py-8 max-w-6xl mx-auto" data-testid="admin-email-settings-page">
      <div className="mb-6">
        <h1 className="font-anton uppercase text-4xl text-white">Microsoft Email Settings</h1>
        <p className="text-sm text-lifted-dim mt-1">
          SMTP wiring for every system email: Lift-Off welcome, affiliate/partner/
          franchisee onboarding, contract delivery, admin notifications. Master Admin only.
        </p>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-6">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`email-tab-${t.key}`}
              className={`px-4 py-2 text-sm uppercase tracking-[0.18em] flex items-center gap-2 border-b-2 ${
                active ? "border-lifted-green text-white" : "border-transparent text-lifted-dim hover:text-white"
              }`}
            >
              <Icon className="h-4 w-4" />{t.label}
            </button>
          );
        })}
      </div>

      {tab === "settings" && <SettingsPanel />}
      {tab === "logs"     && <LogsPanel />}
      {tab === "applepay" && <ApplePayPanel />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SMTP Settings Panel
// ---------------------------------------------------------------------------
function SettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    sender_name: "", sender_email: "", smtp_host: "smtp.office365.com",
    smtp_port: 587, smtp_username: "", smtp_password: "",
    encryption: "tls", reply_to: "", admin_notify: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [testTo, setTestTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      setCfg(data);
      setForm((f) => ({
        ...f,
        sender_name:  data.sender_name  || "",
        sender_email: data.sender_email || "",
        smtp_host:    data.smtp_host    || "smtp.office365.com",
        smtp_port:    data.smtp_port    || 587,
        smtp_username: data.smtp_username || "",
        smtp_password: "",
        encryption:   data.encryption   || "tls",
        reply_to:     data.reply_to     || "",
        admin_notify: data.admin_notify || "",
      }));
      setTestTo(data.admin_notify || data.sender_email || "");
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(""); setErr("");
    try {
      const body = { ...form };
      if (!body.smtp_password) delete body.smtp_password; // never overwrite when blank
      await api.put("/admin/email-settings", body);
      setMsg("Saved. Click 'Send test email' to verify the connection.");
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true); setMsg(""); setErr("");
    try {
      const { data } = await api.post("/admin/email-settings/test",
        { to: testTo, subject: "Lifted SMTP test",
          body: "If you can read this, your Microsoft SMTP connection works." });
      if (data.ok) setMsg(`Test email sent to ${testTo}. Check inbox.`);
      else setErr(`Test failed: ${data.error || data.status}`);
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setTesting(false); }
  };

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-lifted-dim mx-auto mt-12" />;

  return (
    <div className="space-y-5">
      <StatusBanner cfg={cfg} />
      {msg && <Alert kind="ok" text={msg} testid="email-save-ok" />}
      {err && <Alert kind="err" text={err} testid="email-save-err" />}

      <div className="glass-panel p-5 grid md:grid-cols-2 gap-4">
        <Field label="Sender name" value={form.sender_name}
               onChange={(v) => setForm({ ...form, sender_name: v })}
               testid="email-field-sender-name" />
        <Field label="Sender email" type="email" value={form.sender_email}
               onChange={(v) => setForm({ ...form, sender_email: v })}
               testid="email-field-sender-email" />
        <Field label="SMTP host" value={form.smtp_host}
               onChange={(v) => setForm({ ...form, smtp_host: v })}
               testid="email-field-host" />
        <Field label="SMTP port" type="number" value={form.smtp_port}
               onChange={(v) => setForm({ ...form, smtp_port: Number(v) })}
               testid="email-field-port" />
        <Field label="SMTP username" value={form.smtp_username}
               onChange={(v) => setForm({ ...form, smtp_username: v })}
               testid="email-field-username" />
        <Field label={`SMTP password ${cfg?.password_set ? `(saved: ${cfg?.password_mask})` : ""}`}
               type="password" value={form.smtp_password}
               placeholder={cfg?.password_set ? "Leave blank to keep existing" : ""}
               onChange={(v) => setForm({ ...form, smtp_password: v })}
               testid="email-field-password" />
        <Select label="Encryption" value={form.encryption}
                onChange={(v) => setForm({ ...form, encryption: v })}
                options={[
                  { v: "tls",  l: "STARTTLS / TLS  (Office365 recommended)" },
                  { v: "ssl",  l: "SSL (port 465)" },
                  { v: "none", l: "None (testing only)" },
                ]} testid="email-field-encryption" />
        <Field label="Reply-To (optional)" type="email" value={form.reply_to}
               onChange={(v) => setForm({ ...form, reply_to: v })}
               testid="email-field-replyto" />
        <Field label="Admin notification email" type="email" value={form.admin_notify}
               onChange={(v) => setForm({ ...form, admin_notify: v })}
               testid="email-field-adminnotify" />
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={save} disabled={saving}
                data-testid="email-save-button"
                className="btn-lifted inline-flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Save settings
        </button>
        <div className="flex items-center gap-2 ml-auto">
          <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                 placeholder="test@yourdomain.com"
                 data-testid="email-test-to"
                 className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white" />
          <button onClick={sendTest} disabled={testing || !testTo}
                  data-testid="email-test-button"
                  className="btn-lifted-outline inline-flex items-center gap-2 disabled:opacity-50">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send test email
          </button>
        </div>
      </div>

      <div className="text-xs text-lifted-dim">
        <strong>Need an App Password?</strong> Microsoft 365 with MFA requires an App
        Password (not your normal mailbox password). Generate it at{" "}
        <a className="underline" target="_blank" rel="noreferrer"
           href="https://account.microsoft.com/security">account.microsoft.com/security</a>.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email Logs Panel
// ---------------------------------------------------------------------------
function LogsPanel() {
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({ total: 0, sent: 0, failed: 0, pending: 0 });
  const [filter, setFilter] = useState({ status: "", email_type: "", recipient: "" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = {};
      if (filter.status)     params.status = filter.status;
      if (filter.email_type) params.email_type = filter.email_type;
      if (filter.recipient)  params.recipient = filter.recipient;
      const { data } = await api.get("/admin/email-logs", { params });
      setLogs(data.logs || []);
      setCounts(data.counts || {});
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    setResending(id); setErr("");
    try {
      const { data } = await api.post(`/admin/email-logs/${id}/resend`);
      if (!data.ok) setErr(`Resend failed: ${data.error || data.status}`);
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setResending(null); }
  };

  return (
    <div className="space-y-4" data-testid="email-logs-panel">
      <div className="flex flex-wrap gap-3 items-center">
        <CountPill label="Total" n={counts.total} />
        <CountPill label="Sent" n={counts.sent || 0} className="text-emerald-300" />
        <CountPill label="Failed" n={counts.failed || 0} className="text-rose-300" />
        <CountPill label="Pending" n={counts.pending || 0} className="text-amber-300" />
        <button onClick={load} className="ml-auto btn-lifted-outline text-xs inline-flex items-center gap-1"
                data-testid="email-logs-refresh">
          <RefreshCcw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                data-testid="email-logs-filter-status"
                className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white">
          <option value="">All statuses</option>
          <option value="sent">sent</option>
          <option value="failed">failed</option>
          <option value="pending">pending</option>
        </select>
        <input placeholder="Recipient contains…" value={filter.recipient}
               onChange={(e) => setFilter({ ...filter, recipient: e.target.value })}
               data-testid="email-logs-filter-recipient"
               className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white flex-1 min-w-[160px]" />
        <input placeholder="Email type (lift_off_welcome, admin_test, …)" value={filter.email_type}
               onChange={(e) => setFilter({ ...filter, email_type: e.target.value })}
               data-testid="email-logs-filter-type"
               className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white flex-1 min-w-[160px]" />
      </div>

      {err && <Alert kind="err" text={err} />}
      {loading && <Loader2 className="h-5 w-5 animate-spin text-lifted-dim" />}

      <div className="glass-panel overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-white/5 text-lifted-dim uppercase tracking-[0.14em]">
            <tr><th className="text-left p-2">Recipient</th><th className="text-left p-2">Type</th>
                <th className="text-left p-2">Subject</th><th className="text-left p-2">Status</th>
                <th className="text-left p-2">When</th><th className="text-left p-2">Error</th>
                <th className="text-right p-2">Action</th></tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-white/10" data-testid={`email-log-${l.id}`}>
                <td className="p-2 text-white">{l.recipient}</td>
                <td className="p-2 text-lifted-dim">{l.email_type}</td>
                <td className="p-2">{l.subject}</td>
                <td className="p-2"><StatusBadge s={l.status} /></td>
                <td className="p-2 text-lifted-dim">{(l.sent_at || l.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-2 text-rose-300 max-w-[260px] truncate">{l.error_message || ""}</td>
                <td className="p-2 text-right">
                  {l.status === "failed" && (
                    <button onClick={() => resend(l.id)} disabled={resending === l.id}
                            data-testid={`email-resend-${l.id}`}
                            className="btn-lifted-outline text-[10px] inline-flex items-center gap-1">
                      {resending === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
                      Resend
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {logs.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-lifted-dim">No email logs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Apple Pay domain association file Panel
// ---------------------------------------------------------------------------
function ApplePayPanel() {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/apple-pay/domain-association");
      setState(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await api.put("/admin/apple-pay/domain-association", { content: text });
      setMsg("Uploaded. Apple Pay verification file is now hosted at /.well-known/apple-developer-merchantid-domain-association");
      setText("");
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="applepay-panel">
      <div className="glass-panel p-4 text-sm space-y-1">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-lifted-green" />
          <span className="text-white">apple-developer-merchantid-domain-association</span>
        </div>
        <div className="text-xs text-lifted-dim">
          {state?.present
            ? <>Currently <strong className="text-emerald-300">hosted</strong> · last update {(state.updated_at||"").slice(0,19).replace("T"," ")}</>
            : <>Not yet uploaded. Apple Pay buttons will not render until this is hosted.</>}
        </div>
        {state?.preview && (
          <pre className="text-[10px] text-lifted-dim bg-black/40 p-2 rounded mt-2 overflow-x-auto">
            {state.preview}…
          </pre>
        )}
      </div>

      <div className="glass-panel p-4">
        <label className="text-xs uppercase tracking-[0.18em] text-lifted-dim">
          Paste the file contents Square gave you
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="applepay-textarea"
          rows={10}
          placeholder="7B7669643A22302E312E3037..."
          className="w-full mt-2 bg-black/40 border border-white/10 rounded p-2 text-xs text-white font-mono"
        />
        <div className="flex items-center gap-3 mt-3">
          <button onClick={upload} disabled={busy || !text.trim()}
                  data-testid="applepay-upload-button"
                  className="btn-lifted inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload & host
          </button>
          {msg && <span className="text-xs text-emerald-300">{msg}</span>}
          {err && <span className="text-xs text-rose-300">{err}</span>}
        </div>
        <p className="text-[10px] text-lifted-dim mt-3">
          To get this file: <strong>Square Dashboard → Account &amp; Settings → Payment methods → Apple Pay → Add a domain</strong>.
          Download the verification file and paste its raw contents above.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small UI primitives
// ---------------------------------------------------------------------------
function StatusBanner({ cfg }) {
  const ok = cfg?.configured && cfg?.status !== "error";
  return (
    <div className={`glass-panel p-3 border ${ok ? "border-emerald-500/30" : "border-amber-500/40"}`}
         data-testid="email-status-banner">
      <div className="flex items-center gap-2 text-sm">
        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <AlertCircle className="h-4 w-4 text-amber-300" />}
        <span className="text-white uppercase tracking-[0.14em] text-xs">
          Status: {cfg?.status || "not_connected"}
        </span>
        {cfg?.last_test_at && (
          <span className="ml-3 text-xs text-lifted-dim">
            Last test: {(cfg.last_test_at || "").slice(0, 19).replace("T", " ")}
            {cfg.last_test_status === "sent" ? " ✅" : " ❌"}
          </span>
        )}
      </div>
      {cfg?.last_test_error && (
        <div className="text-[11px] text-rose-300 mt-1" data-testid="email-last-error">
          {cfg.last_test_error}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] text-lifted-dim">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder}
             data-testid={testid}
             className="w-full mt-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white" />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] text-lifted-dim">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function CountPill({ label, n, className = "" }) {
  return (
    <div className={`glass-panel px-3 py-1.5 text-xs ${className}`}>
      <span className="text-lifted-dim mr-2">{label}</span>
      <span className="font-anton text-base">{n}</span>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    sent:    "bg-emerald-500/20 text-emerald-300",
    failed:  "bg-rose-500/20 text-rose-300",
    pending: "bg-amber-500/20 text-amber-300",
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${map[s] || "bg-white/10 text-white"}`}>{s}</span>;
}

function Alert({ kind, text, testid }) {
  const cls = kind === "ok"
    ? "border-emerald-500/40 text-emerald-200"
    : "border-rose-500/40 text-rose-200";
  const Icon = kind === "ok" ? CheckCircle2 : XCircle;
  return (
    <div className={`glass-panel p-3 border ${cls} text-sm flex items-start gap-2`} data-testid={testid}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" /><span>{text}</span>
    </div>
  );
}
